import React, { useEffect, useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import Input from '@cloudscape-design/components/input';
import Tabs from '@cloudscape-design/components/tabs';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Select from '@cloudscape-design/components/select';
import Alert from '@cloudscape-design/components/alert';
import Modal from '@cloudscape-design/components/modal';
import Spinner from '@cloudscape-design/components/spinner';
import RadioGroup from '@cloudscape-design/components/radio-group';
import FormField from '@cloudscape-design/components/form-field';
import Avatar from '../components/Avatar';
import { getSupplierControlService } from '../services';
import type {
    ControlSite,
    ControlRequest,
    ControlUser,
    OnboardedSupplier,
    SupplierDetail,
    SupplierOption,
    SupplierEntity,
    SupplierAssignment,
    UserType,
} from '../services/types';

// Supplier Control — internal access management. Ported from the dscs
// prototypes (Suppliers directory + Approval Queue + Supplier Review), wired to
// the SupplierControlService. Data is empty until the backend is built.

type ControlView = 'suppliers' | 'queue' | 'review' | 'users';

function fmtDate(iso: string): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** A name + email cell with an initials avatar, shared by the tables. */
function PartyCell({ name, email, seed }: { name: string; email: string; seed: string }) {
    return (
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Avatar name={name} seed={seed} />
            <SpaceBetween size="xxxs">
                <Box fontWeight="bold">{name}</Box>
                <Box color="text-body-secondary" fontSize="body-s">
                    {email}
                </Box>
            </SpaceBetween>
        </SpaceBetween>
    );
}

// ── Brand drill-down (master -> members -> per-member entity access) ──────────
function SupplierDetailModal({
    admin,
    onClose,
}: {
    admin: OnboardedSupplier;
    onClose: () => void;
}) {
    const [detail, setDetail] = useState<SupplierDetail | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        setDetail(null);
        setError(null);
        if (!admin.supplierId) {
            setError('This supplier admin has no supplier assigned.');
            return undefined;
        }
        getSupplierControlService()
            .getSupplierDetail(admin.supplierId)
            .then((d) => active && setDetail(d))
            .catch((e) => active && setError(e instanceof Error ? e.message : 'Load failed.'));
        return () => {
            active = false;
        };
    }, [admin.supplierId]);

    return (
        <Modal visible onDismiss={onClose} header={`Supplier users · ${admin.company}`} size="large">
            {error ? <Alert type="error">{error}</Alert> : null}
            {!error && detail === null ? <Spinner /> : null}
            {detail ? (
                <SpaceBetween size="l">
                    <Box color="text-body-secondary">
                        {detail.members.length} member(s) · {detail.entityCount} legal entities in
                        this supplier.
                    </Box>
                    {detail.members.map((m) => (
                        <Container
                            key={m.username}
                            header={
                                <Header
                                    variant="h3"
                                    description={m.email}
                                    actions={
                                        <Badge color={m.role === 'MASTER' ? 'green' : 'grey'}>
                                            {m.role}
                                        </Badge>
                                    }
                                >
                                    {m.email}
                                </Header>
                            }
                        >
                            {m.role === 'MASTER' ? (
                                <Box color="text-body-secondary">
                                    Admin — access to all {detail.entityCount} supplier entities.
                                </Box>
                            ) : null}
                            {m.role !== 'MASTER' && m.entities.length === 0 ? (
                                <Box color="text-body-secondary">No entity access granted yet.</Box>
                            ) : null}
                            {m.role !== 'MASTER' && m.entities.length > 0 ? (
                                <SpaceBetween size="xxs">
                                    {m.entities.map((e) => (
                                        <Box key={e.supplierNo}>
                                            <Box fontWeight="bold" display="inline">
                                                {e.supplierNo}
                                            </Box>{' '}
                                            <Box
                                                color="text-body-secondary"
                                                fontSize="body-s"
                                                display="inline"
                                            >
                                                {e.displayLabel}
                                            </Box>
                                            {e.category ? (
                                                <Badge color="blue">{e.category}</Badge>
                                            ) : null}
                                        </Box>
                                    ))}
                                </SpaceBetween>
                            ) : null}
                        </Container>
                    ))}
                </SpaceBetween>
            ) : null}
        </Modal>
    );
}

// ── Onboarded suppliers directory (brand masters) ─────────────────────────────
function SuppliersView({ suppliers }: { suppliers: OnboardedSupplier[] }) {
    const [q, setQ] = useState('');
    const [drillInto, setDrillInto] = useState<OnboardedSupplier | null>(null);
    const query = q.trim().toLowerCase();
    const shown = suppliers.filter(
        (s) =>
            query === '' ||
            s.company.toLowerCase().includes(query) ||
            s.email.toLowerCase().includes(query),
    );

    return (
        <>
            <Table
                variant="container"
                items={shown}
                filter={
                    <TextFilter
                        filteringText={q}
                        filteringPlaceholder="Search by supplier or email…"
                        onChange={({ detail }) => setQ(detail.filteringText)}
                    />
                }
                header={
                    <Header
                        counter={`(${suppliers.length})`}
                        description="Each row is a supplier's admin. Open one to see its members and their legal-entity access."
                    >
                        Onboarded suppliers
                    </Header>
                }
                empty={
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        {suppliers.length === 0
                            ? 'No onboarded suppliers yet. Approve sign-ups from the Approval Queue.'
                            : 'No matching suppliers.'}
                    </Box>
                }
                columnDefinitions={[
                    {
                        id: 'supplier',
                        header: 'Supplier admin',
                        cell: (s) => <PartyCell name={s.company} email={s.email} seed={s.id} />,
                    },
                    {
                        id: 'status',
                        header: 'Status',
                        cell: () => <StatusIndicator type="success">Active</StatusIndicator>,
                    },
                    {
                        id: 'onboarded',
                        header: 'Onboarded',
                        cell: (s) => fmtDate(s.onboarded),
                    },
                    {
                        id: 'actions',
                        header: '',
                        cell: (s) => (
                            <Button
                                variant="normal"
                                disabled={!s.supplierId}
                                onClick={() => setDrillInto(s)}
                            >
                                View members
                            </Button>
                        ),
                    },
                ]}
            />
            {drillInto ? (
                <SupplierDetailModal admin={drillInto} onClose={() => setDrillInto(null)} />
            ) : null}
        </>
    );
}

// ── Team picker (assign-then-approve) ─────────────────────────────────────────
// Shown when the admin approves a pending request. Lists the teams that already
// exist for the login's email domain — each with a few member emails so the
// admin can recognize colleagues — and lets them pick one or create a new
// supplier, then approve. The resolved assignment (existing supplierId or new
// supplierName) is handed back to the caller, which runs the approval.
const NEW_SUPPLIER = '__new__';

// Brand-based approval picker: the admin selects the brand this login belongs
// to (suggested from the email as a clue), sees that brand's legal entities,
// chooses the login's role, then approves. Replaces the old domain-scoped team
// picker — brand, not email domain, is the grouping decision.
function SupplierPickerModal({
    headerText,
    confirmLabel,
    email,
    onCancel,
    onConfirm,
    submitting,
}: {
    headerText: string;
    confirmLabel: string;
    email: string;
    onCancel: () => void;
    onConfirm: (assignment: SupplierAssignment) => void;
    submitting: boolean;
}) {
    const [suppliers, setSuppliers] = useState<SupplierOption[] | null>(null);
    const [suggestedId, setSuggestedId] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selected, setSelected] = useState<string>('');
    const [newName, setNewName] = useState('');
    const [role, setRole] = useState<'MASTER' | 'MEMBER'>('MASTER');

    // The selected brand's legal entities (shown so the approver sees scope).
    const [entities, setEntities] = useState<SupplierEntity[] | null>(null);

    useEffect(() => {
        let active = true;
        setSuppliers(null);
        setLoadError(null);
        getSupplierControlService()
            .listSuppliersForPicker(email)
            .then((res) => {
                if (!active) return;
                setSuppliers(res.suppliers);
                setSuggestedId(res.suggestedSupplierId);
                // Preselect the suggested supplier; else the first; else create-new.
                if (res.suggestedSupplierId) setSelected(res.suggestedSupplierId);
                else if (res.suppliers.length > 0) setSelected(res.suppliers[0].supplierId);
                else setSelected(NEW_SUPPLIER);
            })
            .catch((e) => active && setLoadError(e instanceof Error ? e.message : 'Load failed.'));
        return () => {
            active = false;
        };
    }, [email]);

    // Load the selected brand's entities when a real brand (not create-new) is
    // picked, so the approver can see what they're approving into.
    useEffect(() => {
        let active = true;
        setEntities(null);
        if (!selected || selected === NEW_SUPPLIER) return undefined;
        getSupplierControlService()
            .getSupplierEntities(selected)
            .then((e) => active && setEntities(e))
            .catch(() => active && setEntities([]));
        return () => {
            active = false;
        };
    }, [selected]);

    const confirm = () => {
        if (selected === NEW_SUPPLIER) {
            // A brand-new brand's first login is its master.
            onConfirm({ supplierName: newName.trim(), role: 'MASTER' });
        } else {
            onConfirm({ supplierId: selected, role });
        }
    };

    // The selected supplier (if an existing one) and whether it already has a
    // master — a supplier has exactly one master, so a second MASTER is blocked.
    const selectedSupplier = (suppliers ?? []).find((b) => b.supplierId === selected);
    const selectedHasMaster = Boolean(selectedSupplier?.hasMaster);
    // Trying to assign a MASTER to a supplier that already has one is invalid.
    const masterConflict = selected !== NEW_SUPPLIER && selectedHasMaster && role === 'MASTER';

    const canConfirm =
        (selected === NEW_SUPPLIER ? newName.trim().length > 0 : selected.length > 0) &&
        !masterConflict;

    const supplierOptions = [
        ...(suppliers ?? []).map((b) => ({
            value: b.supplierId,
            label: `${b.supplierName}${b.supplierId === suggestedId ? ' — suggested' : ''}`,
            description: `${b.entityCount} legal ${
                b.entityCount === 1 ? 'entity' : 'entities'
            }${b.hasMaster ? ' · has a master' : ''}`,
        })),
        { value: NEW_SUPPLIER, label: 'Create a new supplier' },
    ];
    const selectedOption = supplierOptions.find((o) => o.value === selected) ?? null;

    return (
        <Modal
            visible
            onDismiss={onCancel}
            header={headerText}
            size="large"
            footer={
                <Box float="right">
                    <SpaceBetween size="xs" direction="horizontal">
                        <Button variant="link" onClick={onCancel} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirm}
                            loading={submitting}
                            disabled={!canConfirm}
                        >
                            {confirmLabel}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                <Box color="text-body-secondary">
                    Assign <strong>{email}</strong> to a supplier. The login shares the
                    supplier&apos;s API credential and legal entities.
                </Box>
                {suggestedId ? (
                    <Alert type="info">
                        Based on the email, this looks like{' '}
                        <strong>
                            {suppliers?.find((b) => b.supplierId === suggestedId)?.supplierName}
                        </strong>
                        . Confirm or pick a different supplier.
                    </Alert>
                ) : null}
                {loadError ? <Alert type="error">{loadError}</Alert> : null}
                {suppliers === null && !loadError ? <Spinner /> : null}
                {suppliers !== null ? (
                    <FormField label="Supplier">
                        <Select
                            selectedOption={selectedOption}
                            options={supplierOptions}
                            onChange={({ detail }) =>
                                setSelected(detail.selectedOption?.value ?? '')
                            }
                        />
                    </FormField>
                ) : null}

                {selected === NEW_SUPPLIER ? (
                    <FormField label="New supplier name">
                        <Input
                            value={newName}
                            placeholder="e.g. Acme Corp"
                            onChange={({ detail }) => setNewName(detail.value)}
                        />
                    </FormField>
                ) : (
                    <>
                        <FormField
                            label="Role at this supplier"
                            description="Admin manages users and all entities. User is granted specific entities by the admin."
                        >
                            <RadioGroup
                                value={role}
                                onChange={({ detail }) =>
                                    setRole(detail.value as 'MASTER' | 'MEMBER')
                                }
                                items={[
                                    {
                                        value: 'MASTER',
                                        label: 'Admin',
                                        disabled: selectedHasMaster,
                                        description: selectedHasMaster
                                            ? 'This supplier already has an admin.'
                                            : undefined,
                                    },
                                    { value: 'MEMBER', label: 'User' },
                                ]}
                            />
                        </FormField>
                        {masterConflict ? (
                            <Alert type="warning">
                                {selectedSupplier?.supplierName} already has an admin. A supplier
                                can have only one admin — approve this login as a <strong>User</strong>,
                                or pick a different supplier.
                            </Alert>
                        ) : null}
                        <FormField label="Legal entities at this supplier">
                            {entities === null ? <Spinner /> : null}
                            {entities !== null && entities.length === 0 ? (
                                <Box color="text-body-secondary" fontSize="body-s">
                                    No legal entities loaded for this supplier yet.
                                </Box>
                            ) : null}
                            {entities !== null && entities.length > 0 ? (
                                <SpaceBetween size="xxxs">
                                    {entities.slice(0, 25).map((e) => (
                                        <Box
                                            key={e.supplierNo}
                                            color="text-body-secondary"
                                            fontSize="body-s"
                                        >
                                            <Box fontWeight="bold" display="inline">
                                                {e.supplierNo}
                                            </Box>{' '}
                                            {e.displayLabel}
                                        </Box>
                                    ))}
                                    {entities.length > 25 ? (
                                        <Box color="text-body-secondary" fontSize="body-s">
                                            …and {entities.length - 25} more
                                        </Box>
                                    ) : null}
                                </SpaceBetween>
                            ) : null}
                        </FormField>
                    </>
                )}
            </SpaceBetween>
        </Modal>
    );
}

// ── Approval queue ────────────────────────────────────────────────────────────
function QueueView({
    requests,
    approvingId,
    onApprove,
}: {
    requests: ControlRequest[];
    approvingId: string | null;
    onApprove: (r: ControlRequest) => void;
}) {
    const [q, setQ] = useState('');
    const query = q.trim().toLowerCase();
    const shown = requests.filter(
        (r) =>
            query === '' ||
            r.email.toLowerCase().includes(query) ||
            r.supplier.toLowerCase().includes(query),
    );

    return (
        <Table
            variant="container"
            items={shown}
            filter={
                <TextFilter
                    filteringText={q}
                    filteringPlaceholder="Search by email or supplier…"
                    onChange={({ detail }) => setQ(detail.filteringText)}
                />
            }
            header={<Header>Approval queue</Header>}
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No pending requests to show yet.
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'supplier',
                    header: 'Supplier',
                    cell: (r) => <PartyCell name={r.supplier} email={r.email} seed={r.id} />,
                },
                {
                    id: 'sites',
                    header: 'Sites',
                    cell: (r) => <Badge color="blue">{`${r.sites} site${r.sites > 1 ? 's' : ''}`}</Badge>,
                },
                {
                    id: 'status',
                    header: 'Status',
                    cell: (r) =>
                        r.verified ? (
                            <StatusIndicator type="pending">
                                Email verified · Awaiting approval
                            </StatusIndicator>
                        ) : (
                            <StatusIndicator type="in-progress">
                                Awaiting email verification
                            </StatusIndicator>
                        ),
                },
                { id: 'submitted', header: 'Submitted', cell: (r) => r.submitted },
                {
                    id: 'actions',
                    header: '',
                    cell: (r) =>
                        r.verified ? (
                            <Button
                                variant="primary"
                                disabled={approvingId !== null}
                                loading={approvingId === r.id}
                                onClick={() => onApprove(r)}
                            >
                                Approve…
                            </Button>
                        ) : (
                            <Box color="text-body-secondary">Not ready</Box>
                        ),
                },
            ]}
        />
    );
}

// ── Supplier review (per-site grant decisions for one pending login) ──────────
type Decision = 'pending' | 'approved' | 'rejected';

function ReviewView({
    request,
    sites,
    onBack,
}: {
    request: ControlRequest | null;
    sites: ControlSite[];
    onBack: () => void;
}) {
    const allTo = (d: Decision): Record<string, Decision> =>
        Object.fromEntries(sites.map((s) => [s.id, d]));
    const [decisions, setDecisions] = useState<Record<string, Decision>>(allTo('pending'));
    const [done, setDone] = useState(false);
    useEffect(() => setDecisions(Object.fromEntries(sites.map((s) => [s.id, 'pending']))), [sites]);

    const set = (id: string, d: Decision) => setDecisions((prev) => ({ ...prev, [id]: d }));
    const allDecided = sites.length > 0 && sites.every((s) => decisions[s.id] !== 'pending');
    const approvedCount = sites.filter((s) => decisions[s.id] === 'approved').length;

    if (!request) {
        return (
            <Container>
                <Box textAlign="center" padding="l">
                    <SpaceBetween size="s">
                        <Box color="text-body-secondary">No request selected.</Box>
                        <Button variant="link" onClick={onBack}>
                            Back to queue
                        </Button>
                    </SpaceBetween>
                </Box>
            </Container>
        );
    }

    if (done) {
        return (
            <Container>
                <Box textAlign="center" padding="xl">
                    <SpaceBetween size="m">
                        <StatusIndicator type="success">Decision recorded</StatusIndicator>
                        <Box color="text-body-secondary">
                            {request.email} was granted access to {approvedCount} of {sites.length}{' '}
                            site(s).
                        </Box>
                        <Button onClick={onBack}>Back to queue</Button>
                    </SpaceBetween>
                </Box>
            </Container>
        );
    }

    return (
        <SpaceBetween size="l">
            <Button variant="link" iconName="angle-left" onClick={onBack}>
                Back to queue
            </Button>

            <Container
                header={
                    <Header variant="h3" description={request.email}>
                        {request.supplier} <StatusIndicator type="pending">Pending login</StatusIndicator>
                    </Header>
                }
            >
                <SpaceBetween size="m">
                    <Box variant="awsui-key-label">
                        Grant site access · {approvedCount} of {sites.length} granted
                    </Box>
                    {sites.length === 0 ? (
                        <Box textAlign="center" color="text-body-secondary" padding="m">
                            No sites to show yet.
                        </Box>
                    ) : (
                        sites.map((s) => {
                            const d = decisions[s.id];
                            return (
                                <Box key={s.id} padding={{ vertical: 'xs' }}>
                                    <SpaceBetween
                                        size="m"
                                        direction="horizontal"
                                        alignItems="center"
                                    >
                                        <SpaceBetween size="xxxs">
                                            <SpaceBetween
                                                size="xs"
                                                direction="horizontal"
                                                alignItems="center"
                                            >
                                                <Box fontWeight="bold">{s.name}</Box>
                                                <Badge>{s.code}</Badge>
                                            </SpaceBetween>
                                            <Box color="text-body-secondary" fontSize="body-s">
                                                {s.location}
                                            </Box>
                                        </SpaceBetween>
                                        <SpaceBetween
                                            size="xs"
                                            direction="horizontal"
                                            alignItems="center"
                                        >
                                            {d !== 'pending' ? (
                                                <StatusIndicator
                                                    type={d === 'approved' ? 'success' : 'error'}
                                                >
                                                    {d === 'approved' ? 'Access: Yes' : 'Access: No'}
                                                </StatusIndicator>
                                            ) : null}
                                            <Button
                                                variant={d === 'approved' ? 'primary' : 'normal'}
                                                onClick={() => set(s.id, 'approved')}
                                            >
                                                Yes
                                            </Button>
                                            <Button
                                                variant={d === 'rejected' ? 'primary' : 'normal'}
                                                onClick={() => set(s.id, 'rejected')}
                                            >
                                                No
                                            </Button>
                                        </SpaceBetween>
                                    </SpaceBetween>
                                </Box>
                            );
                        })
                    )}
                    {sites.length > 0 ? (
                        <SpaceBetween size="xs" direction="horizontal">
                            <Button onClick={() => setDecisions(allTo('approved'))}>
                                Approve all
                            </Button>
                            <Button onClick={() => setDecisions(allTo('rejected'))}>
                                Reject all
                            </Button>
                        </SpaceBetween>
                    ) : null}
                </SpaceBetween>
            </Container>

            <Box textAlign="right">
                <Button
                    variant="primary"
                    disabled={!allDecided}
                    onClick={() => allDecided && setDone(true)}
                >
                    {allDecided ? 'Confirm decisions' : 'Decide all sites first'}
                </Button>
            </Box>
        </SpaceBetween>
    );
}

// ── Users (ops view: supplier vs employee) ───────────────────────────────────
const USER_TYPE_FILTERS: { id: UserType | 'all'; text: string }[] = [
    { id: 'all', text: 'All' },
    { id: 'supplier', text: 'Suppliers' },
    { id: 'employee', text: 'Employees' },
];

function UsersView({
    users,
    onMove,
    movingUsername,
}: {
    users: ControlUser[];
    onMove: (u: ControlUser) => void;
    movingUsername: string | null;
}) {
    const [q, setQ] = useState('');
    const [typeFilter, setTypeFilter] = useState<UserType | 'all'>('all');
    const [roleFilter, setRoleFilter] = useState<'all' | 'MASTER' | 'MEMBER'>('all');

    // A supplier's effective brand role: no stamped role means MEMBER.
    const effectiveRole = (u: ControlUser): string =>
        u.type === 'supplier' ? (u.role || 'MEMBER').toUpperCase() : '';

    const query = q.trim().toLowerCase();
    const shown = users.filter(
        (u) =>
            (typeFilter === 'all' || u.type === typeFilter) &&
            // Role filter applies to suppliers only (employees have no brand role).
            (roleFilter === 'all' ||
                (u.type === 'supplier' && effectiveRole(u) === roleFilter)) &&
            (query === '' ||
                u.email.toLowerCase().includes(query) ||
                u.username.toLowerCase().includes(query)),
    );

    const ROLE_FILTER_OPTIONS = [
        { value: 'all', label: 'All roles' },
        { value: 'MASTER', label: 'Master' },
        { value: 'MEMBER', label: 'Member' },
    ];
    const selectedRoleOption =
        ROLE_FILTER_OPTIONS.find((o) => o.value === roleFilter) ?? ROLE_FILTER_OPTIONS[0];

    const supplierCount = users.filter((u) => u.type === 'supplier').length;
    const employeeCount = users.filter((u) => u.type === 'employee').length;

    return (
        <Table
            variant="container"
            items={shown}
            filter={
                <SpaceBetween size="s">
                    <SpaceBetween size="xs" direction="horizontal">
                        <Badge color="blue">{`${supplierCount} suppliers`}</Badge>
                        <Badge color="grey">{`${employeeCount} employees`}</Badge>
                    </SpaceBetween>
                    <SpaceBetween size="s" direction="horizontal" alignItems="center">
                        <SegmentedControl
                            selectedId={typeFilter}
                            onChange={({ detail }) =>
                                setTypeFilter(detail.selectedId as UserType | 'all')
                            }
                            options={USER_TYPE_FILTERS}
                        />
                        <Select
                            selectedOption={selectedRoleOption}
                            onChange={({ detail }) =>
                                setRoleFilter(
                                    (detail.selectedOption?.value as
                                        | 'all'
                                        | 'MASTER'
                                        | 'MEMBER') ?? 'all',
                                )
                            }
                            options={ROLE_FILTER_OPTIONS}
                        />
                        <TextFilter
                            filteringText={q}
                            filteringPlaceholder="Search by email or username…"
                            onChange={({ detail }) => setQ(detail.filteringText)}
                        />
                    </SpaceBetween>
                </SpaceBetween>
            }
            header={<Header>Users</Header>}
            empty={
                <Box textAlign="center" color="text-body-secondary" padding="l">
                    No users to show yet.
                </Box>
            }
            columnDefinitions={[
                {
                    id: 'user',
                    header: 'User',
                    cell: (u) => (
                        <SpaceBetween size="xxxs">
                            <Box fontWeight="bold">{u.email}</Box>
                            <Box color="text-body-secondary" fontSize="body-s">
                                {u.username}
                            </Box>
                        </SpaceBetween>
                    ),
                },
                {
                    id: 'type',
                    header: 'Type',
                    cell: (u) => (
                        <Badge color={u.type === 'employee' ? 'grey' : 'blue'}>
                            {u.type === 'employee' ? 'Employee' : 'Supplier'}
                        </Badge>
                    ),
                },

                {
                    id: 'supplier',
                    header: 'Supplier',
                    cell: (u) => {
                        if (u.type === 'supplier' && u.supplierId) {
                            return <Badge color="green">{u.supplierId}</Badge>;
                        }
                        return <Box color="text-body-secondary">—</Box>;
                    },
                },
                {
                    id: 'supplierRole',
                    header: 'Brand role',
                    cell: (u) => {
                        // Only suppliers have a brand role; a supplier with no
                        // stamped role is treated as a member.
                        if (u.type !== 'supplier') {
                            return <Box color="text-body-secondary">—</Box>;
                        }
                        const role = (u.role || 'MEMBER').toUpperCase();
                        return (
                            <Badge color={role === 'MASTER' ? 'green' : 'grey'}>{role}</Badge>
                        );
                    },
                },
                {
                    id: 'status',
                    header: 'Status',
                    cell: (u) => (
                        <StatusIndicator type={u.enabled ? 'success' : 'stopped'}>
                            {u.enabled ? u.status : 'Disabled'}
                        </StatusIndicator>
                    ),
                },
                { id: 'created', header: 'Created', cell: (u) => fmtDate(u.created) },
                {
                    id: 'actions',
                    header: '',
                    // Only approved suppliers can be moved between teams.
                    cell: (u) =>
                        u.type === 'supplier' && u.enabled ? (
                            <Button
                                variant="normal"
                                disabled={movingUsername !== null}
                                loading={movingUsername === u.username}
                                onClick={() => onMove(u)}
                            >
                                Change supplier
                            </Button>
                        ) : null,
                },
            ]}
        />
    );
}

// ── Main Control area ─────────────────────────────────────────────────────────
export default function SupplierControlScreen() {
    const [view, setView] = useState<ControlView>('suppliers');
    const [suppliers, setSuppliers] = useState<OnboardedSupplier[]>([]);
    const [requests, setRequests] = useState<ControlRequest[]>([]);
    const [users, setUsers] = useState<ControlUser[]>([]);
    const [reviewId, setReviewId] = useState<string | null>(null);
    const [approvingId, setApprovingId] = useState<string | null>(null);
    const [approveError, setApproveError] = useState<string | null>(null);
    // The pending request currently being assigned a team in the picker modal.
    const [pickFor, setPickFor] = useState<ControlRequest | null>(null);
    // The approved user currently being moved to another team.
    const [moveUser, setMoveUser] = useState<ControlUser | null>(null);
    const [movingUsername, setMovingUsername] = useState<string | null>(null);

    const confirmReassign = (assignment: SupplierAssignment) => {
        if (!moveUser) return;
        const { username } = moveUser;
        setMovingUsername(username);
        getSupplierControlService()
            .reassignSupplier(username, assignment)
            .then(() => {
                setMoveUser(null);
                // Refresh the users list so the new team shows.
                return getSupplierControlService().listUsers().then(setUsers);
            })
            .finally(() => setMovingUsername(null));
    };

    const confirmApprove = (assignment: SupplierAssignment) => {
        if (!pickFor) return;
        const { id } = pickFor;
        setApprovingId(id);
        setApproveError(null);
        getSupplierControlService()
            .approveRequest(id, assignment)
            .then((refreshed) => {
                setRequests(refreshed);
                setPickFor(null);
            })
            .catch((e) =>
                setApproveError(e instanceof Error ? e.message : 'Failed to approve request.'),
            )
            .finally(() => setApprovingId(null));
    };

    useEffect(() => {
        let live = true;
        const svc = getSupplierControlService();
        Promise.all([svc.listOnboardedSuppliers(), svc.listRequests(), svc.listUsers()])
            .then(([s, r, u]) => {
                if (live) {
                    setSuppliers(s);
                    setRequests(r);
                    setUsers(u);
                }
            })
            .catch(() => {
                if (live) {
                    setSuppliers([]);
                    setRequests([]);
                    setUsers([]);
                }
            });
        return () => {
            live = false;
        };
    }, []);

    const reviewRequest = useMemo(
        () => requests.find((r) => r.id === reviewId) ?? null,
        [requests, reviewId],
    );
    // Per-site review needs the sites model, which is a separate follow-up; no
    // sites are available yet, so the review screen shows its empty state.
    const reviewSites: ControlSite[] = useMemo(() => [], []);

    const pendingCount = requests.filter((r) => r.verified).length;

    // The review sub-view is reached from the queue; it isn't its own tab.
    if (view === 'review') {
        return (
            <ReviewView
                request={reviewRequest}
                sites={reviewSites}
                onBack={() => {
                    setView('queue');
                    setReviewId(null);
                }}
            />
        );
    }

    const tabs = (
        <Tabs
            activeTabId={view}
            onChange={({ detail }) => {
                setView(detail.activeTabId as ControlView);
                setReviewId(null);
            }}
            tabs={[
                {
                    id: 'suppliers',
                    label: 'Onboarded Suppliers',
                    content: <SuppliersView suppliers={suppliers} />,
                },
                {
                    id: 'queue',
                    label: pendingCount > 0 ? `Approval Queue (${pendingCount})` : 'Approval Queue',
                    content: (
                        <SpaceBetween size="s">
                            {approveError ? <Alert type="error">{approveError}</Alert> : null}
                            <QueueView
                                requests={requests}
                                approvingId={approvingId}
                                onApprove={setPickFor}
                            />
                        </SpaceBetween>
                    ),
                },
                {
                    id: 'users',
                    label: 'Users',
                    content: (
                        <UsersView
                            users={users}
                            onMove={setMoveUser}
                            movingUsername={movingUsername}
                        />
                    ),
                },
            ]}
        />
    );

    return (
        <>
            {tabs}
            {pickFor ? (
                <SupplierPickerModal
                    headerText={`Assign a brand for ${pickFor.email}`}
                    confirmLabel="Approve"
                    email={pickFor.email}
                    submitting={approvingId === pickFor.id}
                    onCancel={() => (approvingId ? undefined : setPickFor(null))}
                    onConfirm={confirmApprove}
                />
            ) : null}
            {moveUser ? (
                <SupplierPickerModal
                    headerText={`Change brand for ${moveUser.email}`}
                    confirmLabel="Move"
                    email={moveUser.email}
                    submitting={movingUsername === moveUser.username}
                    onCancel={() => (movingUsername ? undefined : setMoveUser(null))}
                    onConfirm={confirmReassign}
                />
            ) : null}
        </>
    );
}
